import { ApiModel } from '@composable-api/api.model'
import { type ApiDateTime, type FormattedCurrency } from '../types/general-data'
import type { GetPriceOptions } from '@simploshop-models/product.model'
import type { VariationPropertyEntry } from '@simploshop-models/cart-item.model'
import { ItemProductFieldModel } from '@simploshop-models/item-product-field.model'

interface Attributes {
    [RefundItemModel.ATTR_ID]: number
    [RefundItemModel.ATTR_NAME]: string
    [RefundItemModel.ATTR_ORDER_ID]: number
    [RefundItemModel.ATTR_ORDER_ITEM_ID]: number
    [RefundItemModel.ATTR_PREFERRED_RESOLVE]: string | null
    [RefundItemModel.ATTR_REASON]: string | null
    [RefundItemModel.ATTR_SKU]: string
    [RefundItemModel.ATTR_AMOUNT]: number
    [RefundItemModel.ATTR_MAX_AMOUNT]: number
    [RefundItemModel.ATTR_TAX_AMOUNT]: FormattedCurrency
    [RefundItemModel.ATTR_TAX_RATE]: FormattedCurrency
    [RefundItemModel.ATTR_TAXED_TOTAL_PRICE]: FormattedCurrency
    [RefundItemModel.ATTR_TAXED_UNIT_PRICE]: FormattedCurrency
    [RefundItemModel.ATTR_TOTAL_PRICE]: FormattedCurrency
    [RefundItemModel.ATTR_UNIT_PRICE]: FormattedCurrency
}

interface Embeds {
    // TODO: add embeds for product URL when BE rework is done
    [RefundItemModel.EMBED_IMAGE_URL]: string
    [RefundItemModel.EMBED_VARIATION_PROPERTIES_NAMES]: Record<string, string> | null
    [RefundItemModel.EMBED_ORDER_ITEM_PRODUCT_FIELDS]: ItemProductFieldModel[]
}

export class RefundItemModel extends ApiModel<Attributes, Embeds> {
    static key = 'RefundItemModel'

    static readonly ATTR_ID = 'id'
    static readonly ATTR_NAME = 'name'
    static readonly ATTR_ORDER_ID = 'order_id'
    static readonly ATTR_ORDER_ITEM_ID = 'order_item_id'
    static readonly ATTR_PREFERRED_RESOLVE = 'preferred_resolve'
    static readonly ATTR_REASON = 'reason'
    static readonly ATTR_SKU = 'sku'
    static readonly ATTR_AMOUNT = 'amount'
    static readonly ATTR_MAX_AMOUNT = 'max_amount'
    static readonly ATTR_TAX_AMOUNT = 'tax_amount'
    static readonly ATTR_TAX_RATE = 'tax_rate'
    static readonly ATTR_TAXED_TOTAL_PRICE = 'taxed_total_price'
    static readonly ATTR_TAXED_UNIT_PRICE = 'taxed_unit_price'
    static readonly ATTR_TOTAL_PRICE = 'total_price'
    static readonly ATTR_UNIT_PRICE = 'unit_price'

    static readonly EMBED_IMAGE_URL = 'image_url'
    static readonly EMBED_VARIATION_PROPERTIES_NAMES = 'variation_properties_names'
    static readonly EMBED_ORDER_ITEM_PRODUCT_FIELDS = 'order_item_product_fields'

    get id() {
        return this._getAttribute(RefundItemModel.ATTR_ID)
    }

    get name() {
        return this._getAttribute(RefundItemModel.ATTR_NAME)
    }

    get orderId() {
        return this._getAttribute(RefundItemModel.ATTR_ORDER_ID)
    }

    get orderItemId() {
        return this._getAttribute(RefundItemModel.ATTR_ORDER_ITEM_ID)
    }

    get preferredResolve() {
        return this._getAttribute(RefundItemModel.ATTR_PREFERRED_RESOLVE)
    }

    get reason() {
        return this._getAttribute(RefundItemModel.ATTR_REASON)
    }

    get sku() {
        return this._getAttribute(RefundItemModel.ATTR_SKU)
    }

    get amount() {
        return this._getAttribute(RefundItemModel.ATTR_AMOUNT)
    }

    get maxAmount() {
        return this._getAttribute(RefundItemModel.ATTR_MAX_AMOUNT)
    }

    get taxAmount() {
        return this._getAttribute(RefundItemModel.ATTR_TAX_AMOUNT)
    }

    get taxRate() {
        return this._getAttribute(RefundItemModel.ATTR_TAX_RATE)
    }

    private get taxedTotalPrice() {
        return this._getAttribute(RefundItemModel.ATTR_TAXED_TOTAL_PRICE)
    }

    private get taxedUnitPrice() {
        return this._getAttribute(RefundItemModel.ATTR_TAXED_UNIT_PRICE)
    }

    private get totalPrice() {
        return this._getAttribute(RefundItemModel.ATTR_TOTAL_PRICE)
    }

    private get unitPrice() {
        return this._getAttribute(RefundItemModel.ATTR_UNIT_PRICE)
    }

    get imageUrl() {
        return this._getEmbed(RefundItemModel.EMBED_IMAGE_URL)
    }

    get variationPropertiesNames() {
        return this._getEmbed(RefundItemModel.EMBED_VARIATION_PROPERTIES_NAMES)
    }

    get orderItemProductFields() {
        return this._getEmbed(RefundItemModel.EMBED_ORDER_ITEM_PRODUCT_FIELDS, ItemProductFieldModel)
    }

    /**
     * Get an array of the variation **property names** as and their **selected attribute names**.
     */
    getValuesOfVariationProperties(): VariationPropertyEntry[] {
        return Object.entries((this.variationPropertiesNames ?? {})).map(([key, value]) => ({
            label: key,
            value: value,
        }))
    }

    getUnitPrice(options?: Partial<GetPriceOptions>) {
        return (options?.tax !== false ? this.taxedUnitPrice?.value : this.unitPrice?.value) ?? null
    }

    getTotalPrice(options?: Partial<GetPriceOptions>) {
        return (options?.tax !== false ? this.taxedTotalPrice?.value : this.totalPrice?.value) ?? null
    }

    getFormattedUnitPrice(options?: Partial<GetPriceOptions>) {
        return (options?.tax !== false ? this.taxedUnitPrice?.formatted : this.unitPrice?.formatted) ?? null
    }

    getFormattedTotalPrice(options?: Partial<GetPriceOptions>) {
        return (options?.tax !== false ? this.taxedTotalPrice?.formatted : this.totalPrice?.formatted) ?? null
    }
}
